import { useEffect, useRef } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { createIsSomeEnum, useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAnalyticsView } from '@/features/amplitude'
import { usePlanChangeAnalytics } from './analytics/use-plan-change-analytics'
import { PLAN_CHANGE_STEP, defaultCadenceFor } from './lib/helpers'
import { fetchPlanOptions } from './lib/planChange.slice'
import {
  selectLastAppliedChange,
  selectPlanOptions,
  selectPlanOptionsFetchedAt,
} from './lib/planChange.selectors'
import { ChoosePlanStep } from './ChoosePlanStep'
import { ConfirmStep } from './ConfirmStep'
import { ResultStep } from './ResultStep'
import { usePlanRecommendation } from './lib/usePlanRecommendation'

const isStep = createIsSomeEnum(PLAN_CHANGE_STEP)

export const PlanChange = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const pageView = useAnalyticsView()
  const stepParam = searchParams.get('step')
  const step = isStep(stepParam) ? stepParam : PLAN_CHANGE_STEP.choose
  const lookupKey: unknown = location.state?.lookupKey
  const isRecommended = location.state?.isRecommended === true
  const lastAppliedChange = useReselector(selectLastAppliedChange)
  const planOptions = useReselector(selectPlanOptions)
  const fetchedAt = useReselector(selectPlanOptionsFetchedAt)
  const { trackOptionsLoaded } = usePlanChangeAnalytics()
  const { recommendation, settled: recommendationSettled } =
    usePlanRecommendation()
  const optionsTrackedRef = useRef(false)
  const mountedAtRef = useRef(Date.now())

  useEffect(() => {
    if (!planOptions || optionsTrackedRef.current) return
    if (fetchedAt === null || fetchedAt < mountedAtRef.current) return
    if (!recommendationSettled) return
    optionsTrackedRef.current = true
    const { currentPlan, targets } = planOptions
    const openingCadence = currentPlan ? defaultCadenceFor(currentPlan) : null
    const badgedTarget = targets.find(
      (target) => target.cadence === openingCadence && target.recommended
    )
    trackOptionsLoaded({
      unavailableReason: planOptions.unavailableReason,
      suppressedTargetCount: planOptions.suppressedTargetCount,
      targetCount: planOptions.targets.length,
      recommendedTier: badgedTarget?.tier ?? null,
    })
  }, [planOptions, fetchedAt, recommendationSettled, trackOptionsLoaded])

  useEffect(() => {
    pageView('plan change page')
  }, [pageView])

  useEffect(() => {
    // Keyed on step, not mount: a cold entry to confirm/result renders a guard
    // redirect rather than the child that would fetch, so gating on the step at
    // mount alone leaves choose with no options and a false error.
    if (step === PLAN_CHANGE_STEP.choose) {
      fetchPlanOptions()(dispatch)
    }
  }, [step, dispatch])

  switch (step) {
    case PLAN_CHANGE_STEP.choose:
      return <ChoosePlanStep recommendation={recommendation} />
    case PLAN_CHANGE_STEP.confirm:
      // The choose grid stays mounted beneath the sheet, but only once options exist.
      // On a cold reload into confirm the sheet's own loader owns the screen.
      return typeof lookupKey === 'string' ? (
        <>
          {planOptions && <ChoosePlanStep recommendation={recommendation} />}
          <ConfirmStep lookupKey={lookupKey} isRecommended={isRecommended} />
        </>
      ) : (
        <Navigate replace to={`/plan-change?step=${PLAN_CHANGE_STEP.choose}`} />
      )
    case PLAN_CHANGE_STEP.result:
      return lastAppliedChange ? (
        <ResultStep result={lastAppliedChange} />
      ) : (
        <Navigate replace to={`/plan-change?step=${PLAN_CHANGE_STEP.choose}`} />
      )
    default:
      return step satisfies never
  }
}
